import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  Chip,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  LinearProgress,
  IconButton,
} from '@mui/material';
import { motion } from 'framer-motion';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const FORMSPREE_ID = 'xwvbpkpb';
const WHATSAPP_NUMBER = '972587489062';

const inputStyle = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 2,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,215,0,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#FFD700' },
  },
  '& .MuiInputBase-input': { color: '#fff' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFD700' },
};

// Input Components - OUTSIDE main component to prevent re-renders
const TextInput = ({ label, field, placeholder, multiline = false, formData, handleChange }) => (
  <TextField
    fullWidth
    label={label}
    value={formData[field] || ''}
    onChange={(e) => handleChange(field, e.target.value)}
    placeholder={placeholder}
    multiline={multiline}
    rows={multiline ? 3 : 1}
    sx={inputStyle}
  />
);

const SelectInput = ({ label, field, options, formData, handleChange }) => (
  <FormControl fullWidth sx={inputStyle}>
    <InputLabel sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-focused': { color: '#FFD700' } }}>{label}</InputLabel>
    <Select
      value={formData[field] || ''}
      onChange={(e) => handleChange(field, e.target.value)}
      label={label}
      sx={{
        background: 'rgba(255,255,255,0.05)',
        color: '#fff',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.2)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,215,0,0.5)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#FFD700' },
        '& .MuiSvgIcon-root': { color: '#FFD700' },
      }}
      MenuProps={{
        PaperProps: {
          sx: { background: '#1a1a1a', color: '#fff' }
        }
      }}
    >
      {options.map((opt, i) => (
        <MenuItem key={i} value={opt} sx={{ '&:hover': { background: 'rgba(255,215,0,0.2)' } }}>{opt}</MenuItem>
      ))}
    </Select>
  </FormControl>
);

const ChipSelect = ({ label, field, options, formData, handleMultiSelect }) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5, fontWeight: 500 }}>{label}</Typography>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {options.map((opt, i) => (
        <Chip
          key={i}
          label={opt}
          onClick={() => handleMultiSelect(field, opt)}
          sx={{
            background: formData[field]?.includes(opt) ? '#FFD700' : 'rgba(255,255,255,0.1)',
            color: formData[field]?.includes(opt) ? '#000' : '#fff',
            fontWeight: 600,
            cursor: 'pointer',
            '&:hover': { background: formData[field]?.includes(opt) ? '#FFC000' : 'rgba(255,255,255,0.2)' },
          }}
        />
      ))}
    </Box>
  </Box>
);

const ClientIntakeForm = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showSummary, setShowSummary] = useState(false);
  const [copied, setCopied] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    businessName: '', businessNameEn: '', businessField: '', whatYouSell: '',
    yearsInBusiness: '', uniqueAdvantages: '', painYouSolve: '', slogan: '',
    phone: '', email: '', address: '', workingHours: '', socialLinks: '',
    idealClient: '', clientEconomicStatus: [], clientProblem: '', whereOnline: [],
    objections: '', whatConvinces: '', language: [],
    mainGoal: '', secondaryGoal: '', specialOffer: '', urgency: '',
    hasLogo: '', primaryColor: '', secondaryColor: '', accentColor: '',
    preferredFonts: '', atmosphere: [], likedWebsites: '', dislikedWebsites: '',
    hasContent: '', contentAmount: '', mainHeadline: '', subHeadlines: '',
    hasTestimonials: '', testimonialCount: '', hasStats: '', statsDetails: '',
    hasCertificates: '', hasFAQ: '',
    hasProfessionalPhotos: '', hasProductPhotos: '', hasVideo: '', useStockPhotos: '', imageStyle: [],
    sections: [], ctaAmount: '', ctaText: '',
    formFields: [], formDestination: '', afterSubmit: '',
    hasDomain: '', domainName: '', hasHosting: '', needSSL: '', needAnalytics: '', needPixel: '', needAccessibility: '',
    trafficSource: [], seoKeywords: '', needThankYouPage: '', needConversionTracking: '',
    deadline: '', hardDeadline: '', budget: '', needMaintenance: '',
    oneThingToRemember: '', strongestReason: '', commonObjection: '', anythingElse: '',
  });

  const steps = [
    { title: 'מידע על העסק', icon: '🏢' },
    { title: 'קהל היעד', icon: '👥' },
    { title: 'מטרות הדף', icon: '🎯' },
    { title: 'עיצוב וברנדינג', icon: '🎨' },
    { title: 'תוכן וטקסטים', icon: '📝' },
    { title: 'תמונות', icon: '📸' },
    { title: 'מבנה הדף', icon: '🏗️' },
    { title: 'טופס לידים', icon: '📋' },
    { title: 'טכני', icon: '⚙️' },
    { title: 'שיווק', icon: '📊' },
    { title: 'זמנים ותקציב', icon: '⏰' },
    { title: 'שאלות בונוס', icon: '💡' },
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelect = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      }
      return { ...prev, [field]: [...current, value] };
    });
  };

  const generateSummary = () => {
    const get = (f) => {
      const v = formData[f];
      if (Array.isArray(v)) return v.join(', ') || 'לא צוין';
      return v || 'לא צוין';
    };
    
    return `
═══════════════════════════════════════════════════════
📋 שאלון אפיון דף נחיתה - ${get('businessName')}
═══════════════════════════════════════════════════════

🏢 מידע על העסק
─────────────────
• שם העסק: ${get('businessName')}
• שם באנגלית: ${get('businessNameEn')}
• תחום: ${get('businessField')}
• מה מוכרים: ${get('whatYouSell')}
• שנות פעילות: ${get('yearsInBusiness')}
• יתרונות ייחודיים: ${get('uniqueAdvantages')}
• בעיה שפותרים: ${get('painYouSolve')}
• סלוגן: ${get('slogan')}
• טלפון: ${get('phone')}
• אימייל: ${get('email')}
• כתובת: ${get('address')}
• שעות פעילות: ${get('workingHours')}
• רשתות חברתיות: ${get('socialLinks')}

👥 קהל היעד
─────────────────
• לקוח אידיאלי: ${get('idealClient')}
• מצב כלכלי: ${get('clientEconomicStatus')}
• בעיה שמחפש לפתור: ${get('clientProblem')}
• איפה נמצא באינטרנט: ${get('whereOnline')}
• התנגדויות נפוצות: ${get('objections')}
• מה ישכנע אותו: ${get('whatConvinces')}
• שפה: ${get('language')}

🎯 מטרות הדף
─────────────────
• מטרה עיקרית: ${get('mainGoal')}
• מטרה משנית: ${get('secondaryGoal')}
• הצעה מיוחדת: ${get('specialOffer')}
• דחיפות: ${get('urgency')}

🎨 עיצוב וברנדינג
─────────────────
• יש לוגו: ${get('hasLogo')}
• צבע ראשי: ${get('primaryColor')}
• צבע משני: ${get('secondaryColor')}
• צבע הדגשה: ${get('accentColor')}
• פונטים: ${get('preferredFonts')}
• אווירה: ${get('atmosphere')}
• אתרים שאוהב: ${get('likedWebsites')}
• אתרים שלא אוהב: ${get('dislikedWebsites')}

📝 תוכן וטקסטים
─────────────────
• יש תוכן מוכן: ${get('hasContent')}
• כמות מידע: ${get('contentAmount')}
• כותרת ראשית: ${get('mainHeadline')}
• תתי כותרות: ${get('subHeadlines')}
• יש המלצות: ${get('hasTestimonials')}
• כמה המלצות: ${get('testimonialCount')}
• יש נתונים/סטטיסטיקות: ${get('hasStats')}
• פרטי נתונים: ${get('statsDetails')}
• יש תעודות/הסמכות: ${get('hasCertificates')}
• יש FAQ: ${get('hasFAQ')}

📸 תמונות
─────────────────
• תמונות מקצועיות: ${get('hasProfessionalPhotos')}
• תמונות מוצר: ${get('hasProductPhotos')}
• סרטון: ${get('hasVideo')}
• תמונות סטוק: ${get('useStockPhotos')}
• סגנון תמונות: ${get('imageStyle')}

🏗️ מבנה הדף
─────────────────
• סקשנים נדרשים: ${get('sections')}
• כמות CTA: ${get('ctaAmount')}
• טקסט לכפתור: ${get('ctaText')}

📋 טופס לידים
─────────────────
• שדות נדרשים: ${get('formFields')}
• לאן נשלח: ${get('formDestination')}
• מה קורה אחרי שליחה: ${get('afterSubmit')}

⚙️ טכני
─────────────────
• יש דומיין: ${get('hasDomain')}
• שם הדומיין: ${get('domainName')}
• יש אחסון: ${get('hasHosting')}
• צריך SSL: ${get('needSSL')}
• צריך אנליטיקס: ${get('needAnalytics')}
• צריך פיקסל: ${get('needPixel')}
• צריך נגישות: ${get('needAccessibility')}

📊 שיווק
─────────────────
• מקורות תנועה: ${get('trafficSource')}
• מילות מפתח SEO: ${get('seoKeywords')}
• צריך דף תודה: ${get('needThankYouPage')}
• צריך מעקב המרות: ${get('needConversionTracking')}

⏰ זמנים ותקציב
─────────────────
• דדליין: ${get('deadline')}
• דדליין קשיח: ${get('hardDeadline')}
• תקציב: ${get('budget')}
• צריך תחזוקה: ${get('needMaintenance')}

💡 שאלות בונוס
─────────────────
• דבר אחד לזכור: ${get('oneThingToRemember')}
• סיבה חזקה לבחור בך: ${get('strongestReason')}
• התנגדות נפוצה ומענה: ${get('commonObjection')}
• משהו נוסף: ${get('anythingElse')}

═══════════════════════════════════════════════════════
📌 שאלון מ-Diamond Landing
═══════════════════════════════════════════════════════
`;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generateSummary());
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const sendToFormspree = async () => {
    const summary = generateSummary();
    const formDataToSend = new FormData();
    formDataToSend.append('subject', `🎯 שאלון אפיון: ${formData.businessName || 'לקוח חדש'}`);
    formDataToSend.append('message', summary);
    formDataToSend.append('businessName', formData.businessName || '');
    formDataToSend.append('phone', formData.phone || '');
    formDataToSend.append('email', formData.email || '');
    
    try {
      const response = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        body: formDataToSend,
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        setSubmitted(true);
        // Meta Pixel - Track Lead
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead');
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const sendToWhatsApp = () => {
    const summary = generateSummary();
    const encodedText = encodeURIComponent(summary);
    // Meta Pixel - Track Lead (WhatsApp is also a lead)
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'Lead');
    }
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodedText}`, '_blank');
  };

  const renderStep = () => {
    switch(currentStep) {
      case 0:
        return (
          <Box>
            <TextInput formData={formData} handleChange={handleChange} label="שם העסק *" field="businessName" placeholder="לדוגמה: דיימונד לנדינג" />
            <TextInput formData={formData} handleChange={handleChange} label="שם העסק באנגלית" field="businessNameEn" placeholder="Diamond Landing" />
            <TextInput formData={formData} handleChange={handleChange} label="תחום העיסוק *" field="businessField" placeholder="במשפט אחד - מה העסק עושה?" />
            <TextInput formData={formData} handleChange={handleChange} label="מה בדיוק אתה מוכר? *" field="whatYouSell" placeholder="מוצר / שירות / ייעוץ / קורס" multiline />
            <TextInput formData={formData} handleChange={handleChange} label="כמה שנים העסק קיים?" field="yearsInBusiness" placeholder="לדוגמה: 5 שנים" />
            <TextInput formData={formData} handleChange={handleChange} label="מה מבדל אותך מהמתחרים? *" field="uniqueAdvantages" placeholder="3 דברים עיקריים" multiline />
            <TextInput formData={formData} handleChange={handleChange} label="מה הבעיה שאתה פותר ללקוח? *" field="painYouSolve" placeholder="איזה כאב או צורך אתה פותר?" multiline />
            <TextInput formData={formData} handleChange={handleChange} label="סלוגן / משפט מפתח" field="slogan" placeholder="אם יש לך משפט שמזוהה איתך" />
            <TextInput formData={formData} handleChange={handleChange} label="טלפון *" field="phone" placeholder="050-000-0000" />
            <TextInput formData={formData} handleChange={handleChange} label="אימייל *" field="email" placeholder="email@example.com" />
            <TextInput formData={formData} handleChange={handleChange} label="כתובת פיזית" field="address" placeholder="אם רלוונטי לעסק" />
            <TextInput formData={formData} handleChange={handleChange} label="שעות פעילות" field="workingHours" placeholder="א-ה 9:00-18:00" />
            <TextInput formData={formData} handleChange={handleChange} label="קישורים לרשתות חברתיות" field="socialLinks" placeholder="פייסבוק, אינסטגרם, לינקדאין..." multiline />
          </Box>
        );
      
      case 1:
        return (
          <Box>
            <TextInput formData={formData} handleChange={handleChange} label="מי הלקוח האידיאלי שלך? *" field="idealClient" placeholder="גיל, מגדר, מיקום, תחומי עניין..." multiline />
            <ChipSelect formData={formData} handleMultiSelect={handleMultiSelect} label="מה המצב הכלכלי שלו?" field="clientEconomicStatus" options={['סטודנט', 'שכיר', 'עצמאי/פרילנסר', 'בעל עסק קטן', 'בעל עסק בינוני-גדול', 'אמיד']} />
            <TextInput formData={formData} handleChange={handleChange} label="מה הבעיה שהוא מחפש לה פתרון? *" field="clientProblem" placeholder="מה מפריע לו? מה הוא רוצה לשנות?" multiline />
            <ChipSelect formData={formData} handleMultiSelect={handleMultiSelect} label="איפה הוא נמצא באינטרנט?" field="whereOnline" options={['פייסבוק', 'אינסטגרם', 'טיקטוק', 'לינקדאין', 'גוגל', 'יוטיוב', 'פורומים', 'ווצאפ גרופים']} />
            <TextInput formData={formData} handleChange={handleChange} label="מה גורם לו לא לקנות? (התנגדויות)" field="objections" placeholder="מה עוצר אותו מלסגור?" multiline />
            <TextInput formData={formData} handleChange={handleChange} label="מה ישכנע אותו לסמוך עליך?" field="whatConvinces" placeholder="המלצות? ניסיון? מחיר?" multiline />
            <ChipSelect formData={formData} handleMultiSelect={handleMultiSelect} label="באיזו שפה הקהל מדבר?" field="language" options={['עברית בלבד', 'עברית + אנגלית', 'אנגלית בלבד', 'רוסית', 'ערבית']} />
          </Box>
        );
      
      case 2:
        return (
          <Box>
            <SelectInput formData={formData} handleChange={handleChange} label="מה המטרה העיקרית של הדף? *" field="mainGoal" options={['השארת פרטים (ליד)', 'שיחת טלפון / וואטסאפ', 'רכישה ישירה', 'הרשמה לניוזלטר / מתנה', 'הורדת קובץ / מדריך', 'הזמנת פגישה / ייעוץ']} />
            <TextInput formData={formData} handleChange={handleChange} label="מטרה משנית" field="secondaryGoal" placeholder="לדוגמה: הצטרפות לניוזלטר" />
            <TextInput formData={formData} handleChange={handleChange} label="האם יש הצעה מיוחדת / מבצע?" field="specialOffer" placeholder="הנחה? מתנה? ייעוץ חינם?" multiline />
            <TextInput formData={formData} handleChange={handleChange} label="האם יש דחיפות?" field="urgency" placeholder="עד תאריך X / רק 10 מקומות..." />
          </Box>
        );
      
      case 3:
        return (
          <Box>
            <SelectInput formData={formData} handleChange={handleChange} label="האם יש לוגו?" field="hasLogo" options={['כן - יש לי קובץ', 'כן - אבל צריך לשפר', 'לא - צריך ליצור', 'לא צריך לוגו']} />
            <TextInput formData={formData} handleChange={handleChange} label="צבע ראשי" field="primaryColor" placeholder="לדוגמה: זהב, כחול כהה, #FFD700" />
            <TextInput formData={formData} handleChange={handleChange} label="צבע משני" field="secondaryColor" placeholder="לדוגמה: שחור, אפור" />
            <TextInput formData={formData} handleChange={handleChange} label="צבע הדגשה" field="accentColor" placeholder="לכפתורים ואלמנטים בולטים" />
            <TextInput formData={formData} handleChange={handleChange} label="פונטים מועדפים" field="preferredFonts" placeholder="אם יש העדפה מסוימת" />
            <ChipSelect formData={formData} handleMultiSelect={handleMultiSelect} label="מה האווירה הרצויה?" field="atmosphere" options={['יוקרתי / פרימיום', 'צעיר / דינמי', 'מקצועי / רציני', 'חם / אישי', 'מינימליסטי / נקי', 'צבעוני / שמח', 'טכנולוגי / חדשני']} />
            <TextInput formData={formData} handleChange={handleChange} label="דוגמאות לאתרים שאתה אוהב" field="likedWebsites" placeholder="2-3 קישורים" multiline />
            <TextInput formData={formData} handleChange={handleChange} label="דוגמאות לאתרים שאתה לא אוהב" field="dislikedWebsites" placeholder="מה לא לעשות" multiline />
          </Box>
        );
      
      case 4:
        return (
          <Box>
            <SelectInput formData={formData} handleChange={handleChange} label="האם יש לך טקסטים מוכנים?" field="hasContent" options={['כן - הכל מוכן', 'חלקי - יש בסיס', 'לא - צריך שתכתוב הכל']} />
            <SelectInput formData={formData} handleChange={handleChange} label="כמה מידע לשים בדף?" field="contentAmount" options={['מינימום - רק הנקודות העיקריות', 'בינוני - הסברים קצרים', 'מפורט - כל הפרטים']} />
            <TextInput formData={formData} handleChange={handleChange} label="כותרת ראשית - מה המסר המרכזי?" field="mainHeadline" placeholder="המשפט הראשון שהלקוח יראה" multiline />
            <TextInput formData={formData} handleChange={handleChange} label="נקודות תומכות / תתי כותרות" field="subHeadlines" placeholder="2-4 נקודות שתומכות במסר" multiline />
            <SelectInput formData={formData} handleChange={handleChange} label="האם יש המלצות מלקוחות?" field="hasTestimonials" options={['כן - עם תמונות ושמות', 'כן - בלי תמונות', 'לא - אבל אפשר להשיג', 'לא']} />
            <TextInput formData={formData} handleChange={handleChange} label="כמה המלצות יש?" field="testimonialCount" placeholder="מספר" />
            <SelectInput formData={formData} handleChange={handleChange} label="האם יש נתונים / סטטיסטיקות?" field="hasStats" options={['כן', 'לא']} />
            <TextInput formData={formData} handleChange={handleChange} label="פרט נתונים" field="statsDetails" placeholder="לקוחות, שנות ניסיון, פרויקטים..." />
            <SelectInput formData={formData} handleChange={handleChange} label="יש תעודות / הסמכות / פרסים?" field="hasCertificates" options={['כן', 'לא']} />
            <SelectInput formData={formData} handleChange={handleChange} label="יש שאלות נפוצות (FAQ)?" field="hasFAQ" options={['כן - יש לי רשימה', 'כן - אבל צריך לכתוב', 'לא צריך']} />
          </Box>
        );
      
      case 5:
        return (
          <Box>
            <SelectInput formData={formData} handleChange={handleChange} label="יש תמונות מקצועיות שלך / של העסק?" field="hasProfessionalPhotos" options={['כן - איכות גבוהה', 'כן - איכות בינונית', 'לא']} />
            <SelectInput formData={formData} handleChange={handleChange} label="יש תמונות מוצר / עבודות קודמות?" field="hasProductPhotos" options={['כן - הרבה', 'כן - מעט', 'לא']} />
            <SelectInput formData={formData} handleChange={handleChange} label="יש סרטון תדמית?" field="hasVideo" options={['כן', 'לא - אבל רוצה', 'לא צריך']} />
            <SelectInput formData={formData} handleChange={handleChange} label="להשתמש בתמונות סטוק?" field="useStockPhotos" options={['כן', 'רק אם צריך', 'לא - רק תמונות אמיתיות']} />
            <ChipSelect formData={formData} handleMultiSelect={handleMultiSelect} label="סגנון תמונות מועדף" field="imageStyle" options={['תמונות אמיתיות', 'אילוסטרציות', 'אייקונים', 'גרפיקה מודרנית', '3D', 'שילוב']} />
          </Box>
        );
      
      case 6:
        return (
          <Box>
            <ChipSelect formData={formData} handleMultiSelect={handleMultiSelect} label="אילו סקשנים צריך בדף?" field="sections" options={['הירו (כותרת + CTA)', 'בעיה / כאב של הלקוח', 'פתרון / איך אני עוזר', 'יתרונות / למה אני', 'איך זה עובד (תהליך)', 'מחירים / חבילות', 'המלצות לקוחות', 'גלריה / פורטפוליו', 'אודות / הסיפור שלי', 'שאלות נפוצות', 'טופס יצירת קשר', 'מפה / כתובת']} />
            <SelectInput formData={formData} handleChange={handleChange} label="כמה כפתורי הנעה לפעולה (CTA)?" field="ctaAmount" options={['מינימום (1-2)', 'בינוני (3-4)', 'הרבה (בכל סקשן)']} />
            <SelectInput formData={formData} handleChange={handleChange} label="מה יהיה כתוב על הכפתור הראשי?" field="ctaText" options={['צור קשר', 'השאר פרטים', 'לפרטים נוספים', 'הזמן עכשיו', 'קבל הצעת מחיר', 'התחל עכשיו']} />
          </Box>
        );
      
      case 7:
        return (
          <Box>
            <ChipSelect formData={formData} handleMultiSelect={handleMultiSelect} label="אילו שדות צריך בטופס?" field="formFields" options={['שם מלא', 'טלפון', 'אימייל', 'שם העסק', 'הודעה חופשית', 'בחירה מרשימה', 'תאריך', 'העלאת קובץ']} />
            <SelectInput formData={formData} handleChange={handleChange} label="לאן הטופס נשלח?" field="formDestination" options={['מייל', 'וואטסאפ', 'מערכת CRM', 'Google Sheets']} />
            <SelectInput formData={formData} handleChange={handleChange} label="מה קורה אחרי שליחה?" field="afterSubmit" options={['הודעת תודה בדף', 'הפניה לדף תודה נפרד', 'הורדת קובץ', 'הפניה לוואטסאפ']} />
          </Box>
        );
      
      case 8:
        return (
          <Box>
            <SelectInput formData={formData} handleChange={handleChange} label="יש דומיין?" field="hasDomain" options={['כן', 'לא - צריך לרכוש']} />
            <TextInput formData={formData} handleChange={handleChange} label="שם הדומיין" field="domainName" placeholder="www.example.co.il" />
            <SelectInput formData={formData} handleChange={handleChange} label="יש אחסון?" field="hasHosting" options={['כן', 'לא - צריך', 'לא יודע']} />
            <SelectInput formData={formData} handleChange={handleChange} label="צריך SSL (https)?" field="needSSL" options={['כן', 'לא', 'לא יודע מה זה']} />
            <SelectInput formData={formData} handleChange={handleChange} label="צריך Google Analytics?" field="needAnalytics" options={['כן', 'לא', 'לא יודע']} />
            <SelectInput formData={formData} handleChange={handleChange} label="צריך פיקסל פייסבוק?" field="needPixel" options={['כן', 'לא', 'לא יודע מה זה']} />
            <SelectInput formData={formData} handleChange={handleChange} label="צריך תאימות לנכים (נגישות)?" field="needAccessibility" options={['כן - חובה', 'כן - רצוי', 'לא צריך']} />
          </Box>
        );
      
      case 9:
        return (
          <Box>
            <ChipSelect formData={formData} handleMultiSelect={handleMultiSelect} label="מאיפה תגיע תנועה לדף?" field="trafficSource" options={['מודעות פייסבוק/אינסטגרם', 'מודעות גוגל', 'אורגני (SEO)', 'וואטסאפ/SMS', 'רשתות חברתיות', 'הפניות', 'כרטיס ביקור/פליירים']} />
            <TextInput formData={formData} handleChange={handleChange} label="מילות מפתח חשובות ל-SEO" field="seoKeywords" placeholder="מה אנשים יחפשו בגוגל?" multiline />
            <SelectInput formData={formData} handleChange={handleChange} label="צריך דף תודה נפרד?" field="needThankYouPage" options={['כן', 'לא']} />
            <SelectInput formData={formData} handleChange={handleChange} label="צריך מעקב המרות?" field="needConversionTracking" options={['כן', 'לא', 'לא יודע']} />
          </Box>
        );
      
      case 10:
        return (
          <Box>
            <TextInput formData={formData} handleChange={handleChange} label="עד מתי צריך את הדף?" field="deadline" placeholder="תאריך או מסגרת זמן" />
            <TextInput formData={formData} handleChange={handleChange} label="יש דדליין קשיח?" field="hardDeadline" placeholder="אירוע? קמפיין? השקה?" multiline />
            <SelectInput formData={formData} handleChange={handleChange} label="מה התקציב?" field="budget" options={['עד 500₪', '500-1,000₪', '1,000-2,000₪', '2,000-5,000₪', 'מעל 5,000₪', 'גמיש']} />
            <SelectInput formData={formData} handleChange={handleChange} label="צריך תחזוקה שוטפת?" field="needMaintenance" options={['כן', 'אולי בעתיד', 'לא']} />
          </Box>
        );
      
      case 11:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3, background: 'rgba(255,215,0,0.1)', color: '#FFD700', border: '1px solid rgba(255,215,0,0.3)', '& .MuiAlert-icon': { color: '#FFD700' } }}>
              💡 שאלות אלו יעזרו ליצור דף שבאמת מוכר! קח רגע לחשוב.
            </Alert>
            <TextInput formData={formData} handleChange={handleChange} label="אם הלקוח יזכור דבר אחד מהדף - מה זה יהיה?" field="oneThingToRemember" placeholder="המסר הכי חשוב" multiline />
            <TextInput formData={formData} handleChange={handleChange} label="מה הסיבה הכי חזקה שמישהו יבחר בך?" field="strongestReason" placeholder="למה דווקא אתה ולא המתחרים?" multiline />
            <TextInput formData={formData} handleChange={handleChange} label="מה ההתנגדות הכי נפוצה ואיך עונים עליה?" field="commonObjection" placeholder="מה עוצר אנשים ומה התשובה שלך" multiline />
            <TextInput formData={formData} handleChange={handleChange} label="יש משהו נוסף שחשוב לך?" field="anythingElse" placeholder="משהו שלא שאלנו ואתה רוצה לציין" multiline />
          </Box>
        );
      
      default:
        return null;
    }
  };

  // Success Screen
  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)', py: 10 }}>
        <Container maxWidth="sm">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}>
            <Card sx={{ background: 'linear-gradient(135deg, rgba(76,175,80,0.2), rgba(0,0,0,0.95))', border: '2px solid rgba(76,175,80,0.5)', borderRadius: 4, p: 5, textAlign: 'center' }}>
              <Typography sx={{ fontSize: '4rem', mb: 2 }}>🎉</Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 2 }}>תודה רבה!</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 4 }}>השאלון נשלח בהצלחה. אחזור אליך בהקדם עם הצעה מותאמת אישית!</Typography>
              <Typography sx={{ color: '#FFD700', fontWeight: 700, mb: 4 }}>💎 Diamond Landing</Typography>
              <Button variant="contained" href="/" sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#000', fontWeight: 700, px: 4, py: 1.5, '&:hover': { background: 'linear-gradient(135deg, #FFC000, #FF8C00)' } }}>חזרה לדף הבית</Button>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  // Summary Screen
  if (showSummary) {
    return (
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)', py: 6 }}>
        <Container maxWidth="md">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
            <Card sx={{ background: 'linear-gradient(135deg, rgba(255,215,0,0.1), rgba(0,0,0,0.95))', border: '2px solid rgba(255,215,0,0.4)', borderRadius: 4, p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography sx={{ fontSize: '3rem', mb: 1 }}>✅</Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 1 }}>השאלון הושלם!</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>בחר איך לשלוח את הפרטים</Typography>
              </Box>
              
              <Box sx={{ background: 'rgba(0,0,0,0.5)', borderRadius: 2, p: 2, mb: 4, maxHeight: 200, overflow: 'auto' }}>
                <Typography component="pre" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{generateSummary()}</Typography>
              </Box>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
                <Button fullWidth variant="contained" startIcon={<WhatsAppIcon />} onClick={sendToWhatsApp} sx={{ background: 'linear-gradient(135deg, #25D366, #128C7E)', py: 2, fontWeight: 700, fontSize: '1.1rem', '&:hover': { background: 'linear-gradient(135deg, #20BD5A, #0E7A60)' } }}>שלח בוואטסאפ</Button>
                <Button fullWidth variant="contained" startIcon={<EmailIcon />} onClick={sendToFormspree} sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#000', py: 2, fontWeight: 700, fontSize: '1.1rem', '&:hover': { background: 'linear-gradient(135deg, #FFC000, #FF8C00)' } }}>שלח למייל</Button>
              </Stack>
              
              <Stack direction="row" spacing={2}>
                <Button fullWidth variant="outlined" startIcon={copied ? <CheckCircleIcon /> : <ContentCopyIcon />} onClick={copyToClipboard} sx={{ borderColor: copied ? '#4CAF50' : 'rgba(255,255,255,0.3)', color: copied ? '#4CAF50' : '#fff', '&:hover': { borderColor: copied ? '#4CAF50' : '#FFD700' } }}>{copied ? 'הועתק!' : 'העתק לעצמי'}</Button>
                <Button fullWidth variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => setShowSummary(false)} sx={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff', '&:hover': { borderColor: '#FFD700' } }}>חזור לעריכה</Button>
              </Stack>
              
              <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.4)', mt: 3, fontSize: '0.85rem' }}>💡 וואטסאפ = תגובה מהירה יותר!</Typography>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  // Main Form
  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)', py: 6 }}>
      <Container maxWidth="sm">
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography sx={{ fontSize: '3rem', mb: 1 }}>💎</Typography>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#fff', mb: 1 }}>שאלון אפיון לדף נחיתה</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)' }}>מלא את הפרטים ונבנה לך דף שמביא תוצאות</Typography>
        </Box>

        {/* Progress */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ color: '#FFD700', fontWeight: 700 }}>{steps[currentStep].icon} {steps[currentStep].title}</Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)' }}>{currentStep + 1} / {steps.length}</Typography>
          </Box>
          <LinearProgress variant="determinate" value={((currentStep + 1) / steps.length) * 100} sx={{ height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.1)', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #FFD700, #FFA500)', borderRadius: 4 } }} />
        </Box>

        {/* Step Indicators */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 4 }}>
          {steps.map((step, i) => (
            <IconButton key={i} onClick={() => setCurrentStep(i)} sx={{ width: 40, height: 40, background: i === currentStep ? '#FFD700' : i < currentStep ? 'rgba(76,175,80,0.3)' : 'rgba(255,255,255,0.1)', color: i === currentStep ? '#000' : i < currentStep ? '#4CAF50' : 'rgba(255,255,255,0.5)', fontSize: '1rem', '&:hover': { background: i === currentStep ? '#FFC000' : 'rgba(255,215,0,0.2)' } }}>
              {i < currentStep ? '✓' : step.icon}
            </IconButton>
          ))}
        </Box>

        {/* Form Card */}
        <Card sx={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 3, p: 4, mb: 4 }}>
          {renderStep()}
        </Card>

        {/* Navigation */}
        <Stack direction="row" spacing={2}>
          {currentStep > 0 && (
            <Button fullWidth variant="outlined" startIcon={<ArrowForwardIcon />} onClick={() => setCurrentStep(prev => prev - 1)} sx={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff', py: 1.5, '&:hover': { borderColor: '#FFD700' } }}>הקודם</Button>
          )}
          
          {currentStep < steps.length - 1 ? (
            <Button fullWidth variant="contained" endIcon={<ArrowBackIcon />} onClick={() => setCurrentStep(prev => prev + 1)} sx={{ background: 'linear-gradient(135deg, #FFD700, #FFA500)', color: '#000', fontWeight: 700, py: 1.5, '&:hover': { background: 'linear-gradient(135deg, #FFC000, #FF8C00)' } }}>הבא</Button>
          ) : (
            <Button fullWidth variant="contained" onClick={() => setShowSummary(true)} sx={{ background: 'linear-gradient(135deg, #4CAF50, #8BC34A)', color: '#fff', fontWeight: 700, py: 1.5, '&:hover': { background: 'linear-gradient(135deg, #43A047, #7CB342)' } }}>🎉 סיים וצור סיכום</Button>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default ClientIntakeForm;
